import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2, Lock, User } from 'lucide-react';

import CarteAuth, { ACCENT, BoutonPrincipal, Champ, ENCRE, Message } from '../components/CarteAuth';
import { adminApi } from '../services/api.jsx';

/* ═══════════════════════════════════════════════════════════════════════════
   PREMIÈRE CONNEXION

   La personne arrive par un lien reçu par e-mail. Elle n'a pas encore de mot
   de passe : elle en choisit un ici, et lui seul le connaîtra.

   L'adresse e-mail n'est PAS redemandée. Elle est déjà en base — c'est elle
   qui a acheminé le lien. La faire ressaisir n'apporterait qu'un risque de
   faute de frappe rendant le compte injoignable.

   Le lien est vérifié à l'ouverture, sans être consommé : un rafraîchissement
   de page ne doit pas l'invalider.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Force du mot de passe, mesurée sur ce qui compte vraiment : la longueur et
 *  la variété. Un indicateur qui exige un caractère spécial et rien d'autre
 *  pousse aux « Motdepasse1! », plus faciles à deviner qu'une phrase longue. */
function mesurer(mot) {
  if (!mot) return { score: 0, libelle: '', couleur: '#E3E9E4' };
  let score = 0;
  if (mot.length >= 8) score += 1;
  if (mot.length >= 12) score += 1;
  if (mot.length >= 16) score += 1;
  const familles = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/]
    .filter((f) => f.test(mot)).length;
  if (familles >= 2) score += 1;
  if (familles >= 3) score += 1;
  const echelle = [
    { libelle: 'Très faible', couleur: '#F04438' },
    { libelle: 'Faible', couleur: '#F04438' },
    { libelle: 'Moyen', couleur: '#F79009' },
    { libelle: 'Bon', couleur: '#F79009' },
    { libelle: 'Solide', couleur: ACCENT },
    { libelle: 'Excellent', couleur: ACCENT },
  ];
  return { score, ...echelle[Math.min(score, 5)] };
}

export default function Activation({ onLogin }) {
  const [parametres] = useSearchParams();
  const navigate = useNavigate();
  const jeton = parametres.get('jeton') || '';

  const [verification, setVerification] = useState({ etat: 'attente', donnees: null, erreur: '' });
  const [form, setForm] = useState({
    first_name: '', last_name: '', mot_de_passe: '', confirmation: '',
  });
  const [visible, setVisible] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let actif = true;
    if (!jeton) {
      setVerification({ etat: 'invalide', donnees: null, erreur: "Lien incomplet." });
      return undefined;
    }
    adminApi.verifierLien(jeton)
      .then((donnees) => {
        if (!actif) return;
        setVerification({ etat: 'valide', donnees, erreur: '' });
        setForm((f) => ({
          ...f,
          first_name: donnees.utilisateur?.first_name || '',
          last_name: donnees.utilisateur?.last_name || '',
        }));
      })
      .catch((e) => actif && setVerification({
        etat: 'invalide', donnees: null, erreur: e.message,
      }));
    return () => { actif = false; };
  }, [jeton]);

  const force = useMemo(() => mesurer(form.mot_de_passe), [form.mot_de_passe]);
  const concordent = form.mot_de_passe && form.mot_de_passe === form.confirmation;

  const soumettre = async (evenement) => {
    evenement.preventDefault();
    setErreur('');
    if (!concordent) {
      setErreur("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setEnvoi(true);
    try {
      const donnees = await adminApi.definirMotDePasse({ jeton, ...form });
      // La session est déjà ouverte par le serveur : on entre directement.
      onLogin?.({ ...donnees.utilisateur, ...donnees });
      // On passe par /login, qui redirige vers la première zone autorisée.
      // Viser /dashboard en dur envoyait un coach sur une page qui lui est
      // fermée — un refus d'accès en guise de bienvenue.
      navigate('/login', { replace: true });
    } catch (e) {
      setErreur(e.message || "L'enregistrement a échoué.");
    } finally {
      setEnvoi(false);
    }
  };

  if (verification.etat === 'attente') {
    return (
      <CarteAuth titre="Vérification du lien">
        <div className="flex items-center justify-center gap-3 py-6 text-[var(--text-muted)] text-[14px]"
             role="status" aria-live="polite">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          Un instant…
        </div>
      </CarteAuth>
    );
  }

  if (verification.etat === 'invalide') {
    return (
      <CarteAuth
        surtitre="Activation"
        titre="Ce lien n'est plus utilisable"
        pied={<button onClick={() => navigate('/mot-de-passe-oublie')}
                      className="font-medium" style={{ color: ACCENT }}>
                Recevoir un nouveau lien
              </button>}
      >
        <div className="flex items-start gap-3 text-[14px] leading-relaxed text-[var(--text-main)]">
          <AlertCircle className="w-[18px] h-[18px] shrink-0 mt-0.5 text-[var(--alerte)]"
                       aria-hidden="true" />
          <p>{verification.erreur}</p>
        </div>
      </CarteAuth>
    );
  }

  const compte = verification.donnees.utilisateur;

  return (
    <CarteAuth
      surtitre={verification.donnees.motif === 'reinitialisation'
        ? 'Nouveau mot de passe' : 'Bienvenue'}
      titre="Choisissez votre mot de passe"
      description={`Vous activez le compte ${compte.username}. Personne d'autre que vous ne connaîtra ce mot de passe.`}
    >
      <form onSubmit={soumettre} className="space-y-5">
        <div className="rounded-pastille px-4 py-3 text-[13px] leading-relaxed"
             style={{ background: '#F7F9F8', border: '1px solid #E3E9E4' }}>
          <span className="text-[var(--text-muted)]">Adresse enregistrée</span>
          <div className="font-medium mt-0.5" style={{ color: ENCRE }}>{compte.email}</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-[12.5px] font-medium text-[var(--text-main)]">Prénoms</label>
            <Champ icone={User} type="text" autoComplete="given-name"
                   placeholder="Ada" value={form.first_name}
                   onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <label className="text-[12.5px] font-medium text-[var(--text-main)]">Nom</label>
            <Champ type="text" autoComplete="family-name"
                   placeholder="Lovelace" value={form.last_name}
                   onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-[12.5px] font-medium text-[var(--text-main)]">Mot de passe</label>
          <div className="relative">
            <Champ icone={Lock} type={visible ? 'text' : 'password'} required
                   autoComplete="new-password" placeholder="Au moins 8 caractères"
                   value={form.mot_de_passe}
                   onChange={(e) => setForm({ ...form, mot_de_passe: e.target.value })} />
            <button type="button" onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? 'Masquer' : 'Afficher'}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]
                               hover:text-[var(--text-main)] transition-colors">
              {visible ? <EyeOff className="w-[17px] h-[17px]" />
                       : <Eye className="w-[17px] h-[17px]" />}
            </button>
          </div>

          {form.mot_de_passe && (
            <div className="pt-1.5">
              <div className="flex gap-1" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span key={i} className="h-[3px] flex-1 rounded-controle transition-colors"
                        style={{ background: i < force.score ? force.couleur : '#E8ECEA' }} />
                ))}
              </div>
              <p className="text-[12px] mt-1.5" style={{ color: force.couleur }}>
                {force.libelle}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="text-[12.5px] font-medium text-[var(--text-main)]">Confirmation</label>
          <div className="relative">
            <Champ icone={Lock} type={visible ? 'text' : 'password'} required
                   autoComplete="new-password" placeholder="Saisissez-le à nouveau"
                   value={form.confirmation}
                   onChange={(e) => setForm({ ...form, confirmation: e.target.value })} />
            {concordent && (
              <CheckCircle2 className="absolute right-4 top-1/2 -translate-y-1/2
                                       w-[17px] h-[17px]" style={{ color: ACCENT }}
                            aria-label="Les mots de passe correspondent" />
            )}
          </div>
          {form.confirmation && !concordent && (
            <p className="text-[12px] text-[var(--alerte)]">Les deux saisies diffèrent.</p>
          )}
        </div>

        <Message>{erreur}</Message>

        <BoutonPrincipal type="submit" disabled={envoi || !concordent} aria-busy={envoi || undefined}>
          {envoi ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />Activation…</> : 'Activer mon compte'}
        </BoutonPrincipal>
      </form>
    </CarteAuth>
  );
}
